/**
 * 嵌入图重生任务：生命周期与 SPA 会话对齐（非组件挂载）。
 * - 离开 AssetDetail / 收起颗粒：请求与 pending 继续
 * - pending 同步写入 sessionStorage，返回后可恢复「生成中」
 * - 若仅有会话 pending、无 inflight（如刷新）：轮询颗粒落库结果做对账
 */
import {
  dajiaoService,
  type GeometryCandidate,
  type GeometryGeneratorId,
} from '../services/dajiaoService';
import { knowledgeService } from '../services/knowledgeService';
import type { KnowledgeParticle } from '../types/domain';
import { readParticleGeometryCandidates } from './embeddedImageMetadata';
import { resolveModelOrder } from './geometryVendorKinds';

const SESSION_CANDIDATES_PREFIX = 'psycheflow.geometryCandidates.v1.';
const SESSION_SLIDE_PREFIX = 'psycheflow.geometrySlide.v1.';
const SESSION_PENDING_KEY = 'psycheflow.geometryPending.v1';

const RECONCILE_INTERVAL_MS = 4_000;
const RECONCILE_MAX_MS = 12 * 60_000;

type PendingJobRecord = {
  particleId: string;
  modelId: GeometryGeneratorId;
  startedAt: number;
  modelOrder: GeometryGeneratorId[];
  ossId?: string;
  modelLabel?: string;
};

export function loadSessionSlideIndex(particleId: string): number | null {
  try {
    const raw = sessionStorage.getItem(`${SESSION_SLIDE_PREFIX}${particleId}`);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
}

export function saveSessionSlideIndex(particleId: string, index: number) {
  try {
    if (!Number.isInteger(index) || index < 0) return;
    sessionStorage.setItem(`${SESSION_SLIDE_PREFIX}${particleId}`, String(index));
  } catch {
    // ignore quota
  }
}

export function clearSessionSlideIndex(particleId: string) {
  try {
    sessionStorage.removeItem(`${SESSION_SLIDE_PREFIX}${particleId}`);
  } catch {
    // ignore
  }
}

export function loadSessionCandidates(particleId: string): GeometryCandidate[] {
  try {
    const raw = sessionStorage.getItem(`${SESSION_CANDIDATES_PREFIX}${particleId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed as GeometryCandidate[] : [];
  } catch {
    return [];
  }
}

export function saveSessionCandidates(particleId: string, list: GeometryCandidate[]) {
  try {
    const usable = list.filter((item) => item?.pngUrl || item?.ossId);
    if (usable.length === 0) return;
    sessionStorage.setItem(`${SESSION_CANDIDATES_PREFIX}${particleId}`, JSON.stringify(list));
  } catch {
    // ignore quota
  }
}

export function clearSessionCandidates(particleId: string) {
  try {
    sessionStorage.removeItem(`${SESSION_CANDIDATES_PREFIX}${particleId}`);
  } catch {
    // ignore
  }
  clearSessionSlideIndex(particleId);
  clearPendingForParticle(particleId);
}

export function mergeCandidates(
  prev: GeometryCandidate[],
  next: GeometryCandidate[],
  order: GeometryGeneratorId[] = resolveModelOrder('GT'),
): GeometryCandidate[] {
  const map = new Map<string, GeometryCandidate>();
  for (const item of prev) {
    if (item?.id) map.set(item.id, item);
  }
  for (const item of next) {
    if (!item?.id) continue;
    const existing = map.get(item.id);
    if (item.pngUrl || item.ossId) {
      map.set(item.id, {
        ...item,
        elapsedMs: item.elapsedMs > 0 ? item.elapsedMs : (existing?.elapsedMs || 0),
      });
      continue;
    }
    if (existing?.pngUrl || existing?.ossId) {
      map.set(item.id, {
        ...existing,
        error: item.error || existing.error,
      });
      continue;
    }
    map.set(item.id, item);
  }
  return order.map((id) => map.get(id)).filter(
    (item): item is GeometryCandidate => Boolean(item),
  );
}

export type GeometryRegenEvent =
  | { type: 'started'; particleId: string; modelId: GeometryGeneratorId }
  | {
      type: 'finished';
      particleId: string;
      modelId: GeometryGeneratorId;
      candidates: GeometryCandidate[];
      particle?: KnowledgeParticle;
      error?: string;
      attemptFailed: boolean;
    };

type JobKey = string;

function jobKey(particleId: string, modelId: GeometryGeneratorId): JobKey {
  return `${particleId}::${modelId}`;
}

const pendingByParticle = new Map<string, Set<GeometryGeneratorId>>();
const pendingMetaByKey = new Map<JobKey, PendingJobRecord>();
const inflight = new Map<JobKey, Promise<void>>();
const listeners = new Set<(event: GeometryRegenEvent) => void>();
const reconcileTimers = new Map<string, ReturnType<typeof setInterval>>();

function emit(event: GeometryRegenEvent) {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch {
      // ignore subscriber errors
    }
  }
}

function readSessionPendingRecords(): PendingJobRecord[] {
  try {
    const raw = sessionStorage.getItem(SESSION_PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is PendingJobRecord => (
      Boolean(item)
      && typeof item === 'object'
      && typeof (item as PendingJobRecord).particleId === 'string'
      && typeof (item as PendingJobRecord).modelId === 'string'
      && Number.isFinite(Number((item as PendingJobRecord).startedAt))
    ));
  } catch {
    return [];
  }
}

function writeSessionPendingRecords(records: PendingJobRecord[]) {
  try {
    if (records.length === 0) {
      sessionStorage.removeItem(SESSION_PENDING_KEY);
      return;
    }
    sessionStorage.setItem(SESSION_PENDING_KEY, JSON.stringify(records));
  } catch {
    // ignore quota
  }
}

function persistPendingSnapshot() {
  const records = Array.from(pendingMetaByKey.values());
  writeSessionPendingRecords(records);
}

function addPending(record: PendingJobRecord) {
  const { particleId, modelId } = record;
  let set = pendingByParticle.get(particleId);
  if (!set) {
    set = new Set();
    pendingByParticle.set(particleId, set);
  }
  set.add(modelId);
  pendingMetaByKey.set(jobKey(particleId, modelId), record);
  persistPendingSnapshot();
}

function removePending(particleId: string, modelId: GeometryGeneratorId) {
  const key = jobKey(particleId, modelId);
  const set = pendingByParticle.get(particleId);
  if (set) {
    set.delete(modelId);
    if (set.size === 0) pendingByParticle.delete(particleId);
  }
  pendingMetaByKey.delete(key);
  persistPendingSnapshot();
}

function clearPendingForParticle(particleId: string) {
  const set = pendingByParticle.get(particleId);
  if (set) {
    for (const modelId of Array.from(set)) {
      pendingMetaByKey.delete(jobKey(particleId, modelId));
    }
    pendingByParticle.delete(particleId);
  }
  persistPendingSnapshot();
  stopReconcile(particleId);
}

/** 模块加载时从会话恢复 pending 标记（无 inflight，需对账） */
function hydratePendingFromSession() {
  for (const record of readSessionPendingRecords()) {
    const key = jobKey(record.particleId, record.modelId);
    if (pendingMetaByKey.has(key)) continue;
    let set = pendingByParticle.get(record.particleId);
    if (!set) {
      set = new Set();
      pendingByParticle.set(record.particleId, set);
    }
    set.add(record.modelId);
    pendingMetaByKey.set(key, {
      ...record,
      modelOrder: Array.isArray(record.modelOrder) && record.modelOrder.length
        ? record.modelOrder
        : resolveModelOrder('GT'),
      startedAt: Number(record.startedAt) || Date.now(),
    });
  }
}

hydratePendingFromSession();

export function getPendingGeometryModels(particleId: string): GeometryGeneratorId[] {
  return Array.from(pendingByParticle.get(particleId) || []);
}

export function getGeometryRegenStartedAt(
  particleId: string,
  modelId: GeometryGeneratorId,
): number | null {
  const startedAt = pendingMetaByKey.get(jobKey(particleId, modelId))?.startedAt;
  return Number.isFinite(startedAt) ? Number(startedAt) : null;
}

export function isGeometryRegenPending(
  particleId: string,
  modelId: GeometryGeneratorId,
): boolean {
  return Boolean(pendingByParticle.get(particleId)?.has(modelId));
}

export function subscribeGeometryRegen(
  listener: (event: GeometryRegenEvent) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const finishedParticleById = new Map<string, KnowledgeParticle>();

/** 离开页面期间完成的落库结果；回来时取走并通知父级 */
export function consumeFinishedParticleUpdate(
  particleId: string,
): KnowledgeParticle | undefined {
  const next = finishedParticleById.get(particleId);
  if (next) finishedParticleById.delete(particleId);
  return next;
}

function candidateReady(item: GeometryCandidate | undefined): boolean {
  return Boolean(item && (item.pngUrl || item.ossId) && !item.error);
}

async function pollParticleForModel(
  particleId: string,
  modelId: GeometryGeneratorId,
  modelOrder: GeometryGeneratorId[],
): Promise<{ particle?: KnowledgeParticle; candidates: GeometryCandidate[]; ready: boolean }> {
  const particle = await knowledgeService.getKnowledgeParticle(particleId);
  const fromMeta = readParticleGeometryCandidates(
    (particle.metadata ?? null) as Record<string, unknown> | null,
  ) as GeometryCandidate[];
  const candidates = mergeCandidates(
    loadSessionCandidates(particleId),
    fromMeta,
    modelOrder,
  );
  saveSessionCandidates(particleId, candidates);
  const hit = candidates.find((item) => item.id === modelId);
  return {
    particle,
    candidates,
    ready: candidateReady(hit),
  };
}

function stopReconcile(particleId: string) {
  const timer = reconcileTimers.get(particleId);
  if (timer) {
    clearInterval(timer);
    reconcileTimers.delete(particleId);
  }
}

/**
 * 对仍标记 pending、但本页无 inflight 的任务做落库轮询（刷新后 / 异常断线）。
 * 有 inflight 时 no-op——原 Promise 会自行收尾。
 */
export function ensureGeometryRegenReconcile(particleId: string): void {
  hydratePendingFromSession();
  const pending = getPendingGeometryModels(particleId);
  if (pending.length === 0) {
    stopReconcile(particleId);
    return;
  }

  const needsReconcile = pending.some(
    (modelId) => !inflight.has(jobKey(particleId, modelId)),
  );
  if (!needsReconcile) {
    stopReconcile(particleId);
    return;
  }

  if (reconcileTimers.has(particleId)) return;

  const startedWall = Date.now();
  const timer = setInterval(() => {
    void (async () => {
      const models = getPendingGeometryModels(particleId).filter(
        (modelId) => !inflight.has(jobKey(particleId, modelId)),
      );
      if (models.length === 0) {
        stopReconcile(particleId);
        return;
      }
      if (Date.now() - startedWall > RECONCILE_MAX_MS) {
        for (const modelId of models) {
          const meta = pendingMetaByKey.get(jobKey(particleId, modelId));
          const order = meta?.modelOrder || resolveModelOrder('GT');
          removePending(particleId, modelId);
          emit({
            type: 'finished',
            particleId,
            modelId,
            candidates: loadSessionCandidates(particleId),
            error: '生成超时或已中断，请重试',
            attemptFailed: true,
          });
          void order;
        }
        stopReconcile(particleId);
        return;
      }

      for (const modelId of models) {
        const meta = pendingMetaByKey.get(jobKey(particleId, modelId));
        const order = meta?.modelOrder || resolveModelOrder('GT');
        try {
          const polled = await pollParticleForModel(particleId, modelId, order);
          if (!polled.ready) continue;
          if (polled.particle) finishedParticleById.set(particleId, polled.particle);
          removePending(particleId, modelId);
          emit({
            type: 'finished',
            particleId,
            modelId,
            candidates: polled.candidates,
            particle: polled.particle,
            attemptFailed: false,
          });
        } catch {
          // 下一轮再试
        }
      }

      if (getPendingGeometryModels(particleId).every(
        (modelId) => inflight.has(jobKey(particleId, modelId)),
      ) || getPendingGeometryModels(particleId).length === 0) {
        stopReconcile(particleId);
      }
    })();
  }, RECONCILE_INTERVAL_MS);

  reconcileTimers.set(particleId, timer);
}

/** 发起重生；已在跑则 no-op 返回 false。请求随 SPA 会话存活，不依赖组件挂载。 */
export function startGeometryRegen(input: {
  particleId: string;
  modelId: GeometryGeneratorId;
  ossId?: string;
  modelOrder: GeometryGeneratorId[];
  modelLabel?: string;
  seedCandidates?: GeometryCandidate[];
}): boolean {
  const { particleId, modelId, ossId, modelOrder, modelLabel, seedCandidates } = input;
  const key = jobKey(particleId, modelId);
  if (inflight.has(key)) return false;

  addPending({
    particleId,
    modelId,
    startedAt: Date.now(),
    modelOrder: [...modelOrder],
    ossId,
    modelLabel,
  });
  emit({ type: 'started', particleId, modelId });

  const promise = (async () => {
    let candidates = mergeCandidates(
      loadSessionCandidates(particleId),
      seedCandidates || [],
      modelOrder,
    );
    saveSessionCandidates(particleId, candidates);

    let particle: KnowledgeParticle | undefined;
    let error: string | undefined;
    let attemptFailed = false;

    try {
      const result = await dajiaoService.generateCandidates({
        particleId,
        ossId,
        models: [modelId],
      });

      const attempt = (result.attempts || result.candidates).find((item) => item.id === modelId);
      const qwenPathTampered = modelId === 'dajiao-qwen-agent' && Boolean(
        attempt
        && (attempt.provider !== 'qwen+dajiao-mcp' || /dinogeo/i.test(String(attempt.model || ''))),
      );
      attemptFailed = Boolean(
        attempt?.error
        || !attempt
        || (!attempt.pngUrl && !attempt.ossId)
        || qwenPathTampered,
      );

      if (attemptFailed) {
        candidates = mergeCandidates(
          candidates,
          attempt
            ? [{
                ...attempt,
                pngUrl: null,
                ossId: null,
                assetId: null,
                error: attempt.error || '生成失败',
              }]
            : [],
          modelOrder,
        );
        saveSessionCandidates(particleId, candidates);
        error = qwenPathTampered
          ? 'DQA 未走 Qwen→MCP 链路（服务端可能未部署新代码，或错误回退到了 dinogeo）'
          : (attempt?.error || `${modelLabel || modelId} 生成失败`);
      } else {
        candidates = mergeCandidates(candidates, result.candidates, modelOrder);
        saveSessionCandidates(particleId, candidates);
        particle = result.particle;
        if (particle) finishedParticleById.set(particleId, particle);
      }
    } catch (regenErr) {
      attemptFailed = true;
      error = regenErr instanceof Error ? regenErr.message : '生成图片失败';
    } finally {
      removePending(particleId, modelId);
      inflight.delete(key);
      emit({
        type: 'finished',
        particleId,
        modelId,
        candidates,
        particle,
        error,
        attemptFailed,
      });
    }
  })();

  inflight.set(key, promise);
  return true;
}
